import React, { useState } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button
} from '@amzn/awsui-components-console';
import './ActionCard.scss';

export interface ActionButton {
  label: string;
  style: 'primary' | 'normal';
  action: string;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export interface ActionCardProps {
  title?: string;
  description: string;
  buttons: ActionButton[];
  state?: 'active' | 'loading' | 'disabled';
  onButtonClick?: (action: string) => void;
}

/**
 * ActionCard - A prominent card for decision points
 * 
 * Displays a card with title, description, and action buttons.
 * Used when the agent presents results and needs user direction.
 * 
 * Examples:
 * - After access check: "Generate Job" or "Make Changes"
 * - After validation: "Proceed" or "Modify"
 * - After results: "Download" or "View Details"
 */
export const ActionCard: React.FC<ActionCardProps> = ({
  title,
  description,
  buttons,
  state = 'active',
  onButtonClick
}) => {
  const [clickedButton, setClickedButton] = useState<string | null>(null);

  const handleButtonClick = (button: ActionButton) => {
    if (state === 'disabled' || button.disabled) return;
    
    setClickedButton(button.action);

    if (button.action.startsWith('http://') || button.action.startsWith('https://')) {
      window.open(button.action, '_blank', 'noopener,noreferrer');
      setTimeout(() => setClickedButton(null), 500);
      return;
    }
    
    if (button.onClick) {
      button.onClick();
    }
    
    if (onButtonClick) {
      onButtonClick(button.action);
    }
  };

  const isDisabled = state === 'disabled';
  const isLoading = state === 'loading';

  return (
    <div className={`action-card action-card--${state}`}>
      <Container
        header={
          title ? (
            <Header variant="h3">
              {title}
            </Header>
          ) : undefined
        }
      >
        <SpaceBetween size="l">
          {/* Description */}
          <div className="action-card__description">
            {description}
          </div>

          {/* Action Buttons */}
          <div className="action-card__buttons">
            <SpaceBetween direction="horizontal" size="s">
              {buttons.map((button, index) => (
                <Button
                  key={index}
                  variant={button.style === 'primary' ? 'primary' : 'normal'}
                  onClick={() => handleButtonClick(button)}
                  disabled={isDisabled || button.disabled}
                  loading={
                    (isLoading && clickedButton === button.action) || 
                    button.loading
                  }
                  iconAlign="right"
                  iconName={
                    button.action.startsWith('http://') || button.action.startsWith('https://')
                      ? 'external'
                      : undefined
                  }
                >
                  {button.label}
                </Button>
              ))}
            </SpaceBetween>
          </div>
        </SpaceBetween>
      </Container>
    </div>
  );
};

export default ActionCard;
